import { Skeleton, Text } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowDownRight,
  IconArrowRight,
  IconArrowUpRight,
  IconCalendarOff,
  IconPlugConnectedX
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  deltaArrow,
  deltaTone,
  formatKpiDeltaAbsolute,
  formatKpiDeltaPercent,
  formatKpiValue,
  sanitizeKpiHref,
  valueTone,
  type KpiData,
  type KpiFormat,
  type KpiGoodDirection,
  type KpiThresholds
} from './kpiData';
import type { KpiState } from './kpiState';
import { WidgetLink } from './WidgetLink';
import classes from './KpiWidget.module.css';
import { componentsTestIds } from '@components/testids';

/**
 * TV1-1930 (S15) — the KPI / big-number widget.
 *
 * One measure, one number, and (optionally) how it moved against the immediately
 * preceding period of the same length. Mobile-capable — a big number is the most
 * mobile-friendly widget there is.
 *
 * **Delta: % change is primary, absolute is secondary.** Both are computed; the
 * percentage is what the eye should land on, so it carries the weight and the
 * (opt-in) colour, and the absolute change sits beside it, always dimmed.
 *
 * **Colour is opt-in.** `goodDirection` defaults to `'neutral'`, which renders an
 * arrow and a number and NO colour. Nothing in the domain knows a measure's
 * polarity — for `returnRate` / `cancellationRate` up is BAD — and a confidently
 * green rising return-rate tile is worse than an uncoloured one. When the author
 * opts in with `'down'`, the tone is inverted: falling is good, and painted so.
 *
 * ## ⚠️ TV1-2291 (t15) — THE WALL OF EM-DASHES, and its end
 *
 * On the ticket owner's real account **three of the seven headline tiles show no
 * value at all and four of the five that could show a delta show none** — and
 * every one of those absences used to render as the same `—`. That is the
 * substance of *"el dashboard se ve feo"*: no amount of card polish fixes a
 * headline row that is mostly em-dashes, because it looks broken however well it
 * is drawn.
 *
 * `design-system.md` makes empty states MANDATORY (*"Never show a blank area —
 * always communicate what belongs there and why it's empty"*), and this tile was
 * in breach of that rule. It now renders **four distinct treatments**, one per
 * cause, decided by `resolveKpiState` (`kpiState.ts`) rather than by this file:
 *
 * | state | treatment | why |
 * |---|---|---|
 * | `prerequisite-missing` | amber block, plug icon, the tile's OWN caption as the sentence | something must be set up first; naming it beats a dash |
 * | `empty-period` | neutral block, calendar icon | "no sales this period" is a REAL ANSWER, so it is styled as information, never as a failure |
 * | `no-prior-window` | the value, and NO delta row at all | ADR-051 forbids fabricating a delta; a placeholder dash for it reads as breakage. Silence is the correct rendering |
 * | `error` | red block naming the failure | broken is not empty |
 *
 * **This component NEVER prints an em-dash.** The value is only rendered in the
 * two states where a value exists, and the delta is only rendered where it was
 * measured — so there is no code path left that reaches `KPI_EMPTY`. That is the
 * invariant SC7 asks for, and it is asserted the blunt way in `KpiWidget.test.tsx`
 * (the glyph must not appear in the rendered text of any of the seven tiles).
 *
 * ⚠️ **NO NEW COPY, NO LINKS** (owner decision, 2026-08-14). The prerequisite
 * sentence is the tile's existing caption, verbatim, supplied as DATA by
 * `curatedReports.ts`. Nothing here authors remediation prose or wires a
 * destination; `prerequisite.link` is deliberately not rendered as an anchor yet.
 *
 * ⚠️ **The generic error tile stands.** `WebWidget` renders `WidgetErrorTile`
 * for a failed fetch BEFORE the parser runs, so in the app a KPI's error state is
 * that shared red alert — which already names the Cube member it could not
 * resolve, and is consistent with every other widget type. The `error` branch
 * below is therefore defence in depth for a caller that hands this component a
 * state it resolved itself: it must not fall through to the value path and print
 * a dash.
 */

export type KpiWidgetProps = KpiData & {
  format?: KpiFormat;
  currency?: string;
  decimals?: number;
  goodDirection?: KpiGoodDirection;
  /** Optional label under the number (the widget frame already renders the title). */
  caption?: string;
  /**
   * 🔗 THE LINK GATE (2026-08-19). `true` renders the value as PLAIN TEXT even when
   * `href` is present and valid — see `AlertWidget` for the full reasoning. Short
   * version: in the editor a card is an object you are ARRANGING, and a click that
   * navigates away loses the arrangement.
   */
  editMode?: boolean;
  /** Explicit locale for deterministic tests; defaults to the runtime locale. */
  locale?: string;
  /**
   * PREFACTOR-grammar — colour the VALUE against a band (`good <= threshold < bad`),
   * independently of whether a delta exists. See `KpiThresholds` in `kpiData.ts`
   * for why it is two bands and not three.
   */
  thresholds?: KpiThresholds;
  /**
   * PREFACTOR-grammar — render the measure as TEXT rather than as a number.
   *
   * For a KPI pointed at a non-numeric member — MercadoLibre's reputation level is
   * the literal string `'5_green'` — the numeric path yields `NaN` -> `null`, i.e.
   * the tile silently cannot show the one thing it exists to show.
   */
  valueMode?: 'number' | 'text';
  /**
   * PREFACTOR-grammar — make the value a link (an operational KPI is a call to
   * action: "12 orders to fulfil" should take you to the 12 orders).
   *
   * ⚠️ TV1-1934 — **UNTRUSTED INPUT.** Anyone who can author a widget in the manual
   * builder can set this, and widget layouts are shared across the account. It is
   * scheme-allow-listed by `sanitizeKpiHref` at render: in-app (`/…`) goes through
   * react-router's `<Link>`, `http(s):` becomes a plain `<a>`, and **everything else
   * is not a link at all** — the value renders as plain text.
   *
   * A KPI without `href` renders exactly as it did before: no anchor, no wrapper.
   */
  href?: string;
  /**
   * TV1-2291 — WHY this tile looks the way it does, from `resolveKpiState`.
   *
   * OPTIONAL, and the fallback is not a convenience: `MobileWidget`'s props
   * builder does not thread a resolved state through, and a bare `<KpiWidget>` is
   * a legal call. Both must still obey SC7, so an absent state is DERIVED from the
   * shaped data below — the same value/delta axis `resolveKpiState` walks, minus
   * the witness and declaration branches it has no data for. The derived verdict is
   * therefore the one that claims least (`empty-period`, `basis: 'default'`), never
   * a dash.
   */
  state?: KpiState;
  /**
   * TV1-2392 (slice 6) — SOME of the rows behind this KPI resolved no umbral at
   * all, so the displayed count (even a confident-looking small number) is not
   * the whole picture. Rendered as a small, honest line — independent of `state`,
   * because it must show alongside a `ready` value, which is exactly the case
   * the generic empty-state machinery in `kpiState.ts` cannot reach (it only
   * fires while the DISPLAYED value is itself unmeasured).
   */
  partialCoverage?: boolean;
};

const TONE_CLASS = {
  positive: classes.tonePositive,
  negative: classes.toneNegative,
  neutral: classes.toneNeutral
} as const;

const ARROW_ICON = {
  up: IconArrowUpRight,
  down: IconArrowDownRight,
  flat: IconArrowRight
} as const;

/**
 * The value/delta axis of `resolveKpiState`, for callers that did not resolve one.
 *
 * Kept deliberately literal — it MIRRORS `resolveKpiState`'s `hasValue` test
 * (including the `Number.isFinite` guard, without which a non-finite value would
 * produce a `ready` tile whose number formats to the very em-dash this redesign
 * exists to remove) and its no-prior-window reason. It cannot reach
 * `prerequisite-missing`: the witness lives in the Cube rows, which are long gone
 * by the time props are built.
 */
const deriveKpiState = (
  value: number | null,
  textValue: string | null,
  delta: KpiData['delta'],
  hasCompare: boolean,
  isTextMode: boolean
): KpiState => {
  const hasValue = isTextMode
    ? textValue !== null
    : value !== null && Number.isFinite( value );

  if ( !hasValue ) return { kind: 'empty-period', basis: 'default' };
  if ( delta.kind !== 'unknown' ) return { kind: 'ready' };

  return {
    kind: 'no-prior-window',
    reason: hasCompare ? 'prior-window-empty' : 'no-compare-requested'
  };
};

/**
 * The design-system empty state (`design-system.md` § Empty States, pattern 2):
 * an icon in a circle, a 13px/600 title, a 12px dimmed description.
 *
 * `tone` is what makes the three causes tell themselves apart at a glance without
 * the reader having to read: **amber** for "something is missing, set it up",
 * **neutral** for "this period was genuinely empty" (information, NOT a failure —
 * that distinction is the entire point), **red** for "the query broke".
 */
const KpiStateBlock = ({
  kind,
  tone,
  icon,
  title,
  description
}: {
  kind: KpiState['kind'];
  tone: 'attention' | 'neutral' | 'danger';
  icon: ReactNode;
  title: string;
  description?: string;
}) => (
  <div
    className={classes.stateBlock}
    data-testid={componentsTestIds.kpiState( kind )}
    data-kpi-tone={tone}
  >
    <div className={classes.stateIcon}>{icon}</div>
    <Text component="div" className={classes.stateTitle} data-testid={componentsTestIds.kpiStateTitle}>
      {title}
    </Text>
    {description ? (
      <Text
        component="div"
        className={classes.stateDescription}
        data-testid={componentsTestIds.kpiStateDescription}
      >
        {description}
      </Text>
    ) : null}
  </div>
);

/**
 * TV1-2392 (slice 6) — the partial-coverage disclosure. Small and honest: not an
 * alert, not a colour change, just a line naming the gap so a confident-looking
 * number cannot be mistaken for complete coverage.
 *
 * ⚠️ THE WORDING MUST BE TRUE IN BOTH CASES. The witness only says "at least one row
 * resolved no umbral" — it does not distinguish SOME from ALL, and the fully
 * unresolved case (`value === null`, the `prerequisite` state) renders this same line.
 * "Algunos ítems" was therefore false exactly when the gap was TOTAL, which is the
 * case where an understated disclosure does the most damage. "Hay ítems …" states the
 * existence claim the witness actually supports and reads correctly either way.
 */
const PartialCoverageNote = ({ t }: { t: ( key: string, fallback: string ) => string }) => (
  <Text
    component="div"
    className={classes.stateDescription}
    data-testid={componentsTestIds.kpiPartialCoverage}
  >
    {t(
      'widget_kpi_partial_coverage',
      'Hay ítems sin un umbral de stock configurado; no están contados aquí.'
    )}
  </Text>
);

export const KpiWidget = ({
  value,
  textValue = null,
  delta,
  hasCompare,
  format = 'number',
  currency,
  decimals,
  goodDirection = 'neutral',
  caption,
  locale,
  thresholds,
  valueMode = 'number',
  href,
  editMode = false,
  state,
  partialCoverage = false
}: KpiWidgetProps ) => {
  const { t } = useTranslation( 'widgets' );

  const isTextMode = valueMode === 'text';
  const kpiState = state ?? deriveKpiState( value, textValue, delta, hasCompare, isTextMode );

  const renderTile = ( body: ReactNode, extra?: Record<string, string | undefined> ) => (
    <div
      className={classes.container}
      data-testid={componentsTestIds.kpiWidget}
      // The cause, on the tile itself. A reader with dev-tools open — and every
      // test in this suite — can tell a measured verdict from an asserted one
      // without re-deriving it.
      data-kpi-state={kpiState.kind}
      {...extra}
    >
      {body}
    </div>
  );

  // Waiting is not empty. A tile that has not been answered yet must never flash
  // "no activity in this period" — that would be a claim about data we do not have.
  if ( kpiState.kind === 'loading' ) {
    return renderTile( <Skeleton height="60%" width="70%" radius="sm" /> );
  }

  if ( kpiState.kind === 'error' ) {
    return renderTile(
      <KpiStateBlock
        kind="error"
        tone="danger"
        icon={<IconAlertTriangle size={18} stroke={1.7} />}
        title={t( 'widget_kpi_state_error_title', 'This card could not be loaded' )}
        description={kpiState.message || t( 'widget_kpi_state_error_description', 'The query failed.' )}
      />
    );
  }

  if ( kpiState.kind === 'prerequisite-missing' || kpiState.kind === 'empty-period' ) {
    const isPrerequisite = kpiState.kind === 'prerequisite-missing';

    // ⚠️ `prerequisite: null` on a WITNESS basis is the case that states the
    // opposite of the truth if it is got wrong. The witness PROVED the window had
    // activity; the author simply never wrote the sentence. So the tile says the
    // value cannot be computed — saying "no activity" here would contradict the
    // measurement it was just handed.
    const authored = isPrerequisite ? kpiState.prerequisite : null;

    const title = isPrerequisite
      ? authored
        ? t( 'widget_kpi_state_prerequisite_title', 'Not available yet' )
        : t( 'widget_kpi_state_uncomputable_title', 'Cannot be computed' )
      : t( 'widget_kpi_state_empty_period_title', 'No activity in this period' );

    const description = isPrerequisite
      ? authored
        // NO NEW COPY (owner decision): the tile's own caption, verbatim, rendered
        // as the legible explanation it always was instead of a footnote under a
        // bare dash. No link — nothing routes anywhere yet, and a link that goes
        // nowhere is worse than a sentence that names the missing step.
        ? authored.message
        : t(
          'widget_kpi_state_uncomputable_description',
          'This period had activity, but this value cannot be computed.'
        )
      : t( 'widget_kpi_state_empty_period_description', 'The query ran: there is nothing to report here.' );

    return renderTile(
      <>
        <KpiStateBlock
          kind={kpiState.kind}
          tone={isPrerequisite ? 'attention' : 'neutral'}
          icon={isPrerequisite ? <IconPlugConnectedX size={18} stroke={1.7} /> : <IconCalendarOff size={18} stroke={1.7} />}
          title={title}
          description={description}
        />

        {/* The caption is suppressed when it IS the sentence above — which is the
            normal case for a curated tile, whose prerequisite message is its own
            caption verbatim. Printing it twice would be noise. */}
        {caption && caption !== description ? (
          <Text component="div" className={classes.caption} data-testid={componentsTestIds.kpiCaption}>
            {caption}
          </Text>
        ) : null}

        {partialCoverage ? <PartialCoverageNote t={t} /> : null}
      </>,
      { 'data-kpi-basis': kpiState.basis }
    );
  }

  // ── The two states that HAVE a number ──────────────────────────────────────
  const tone = deltaTone( delta, goodDirection );
  const arrow = deltaArrow( delta );
  const ArrowIcon = arrow ? ARROW_ICON[arrow] : null;

  const valueOptions = { locale, currency, decimals };

  // PREFACTOR-grammar — the VALUE's own tone, decoupled from `hasCompare`. A
  // snapshot metric (seller reputation has no history, by construction) can now be
  // coloured; before, no compare meant no colour at all. `neutral` whenever no
  // thresholds are configured, so every KPI that shipped before this is unchanged.
  const bandTone = valueTone( value, thresholds );

  // Reached ONLY in `ready` / `no-prior-window`, i.e. only when the value is known
  // — so neither branch can produce `KPI_EMPTY`.
  const renderedValue = isTextMode ? textValue : formatKpiValue( value, format, valueOptions );

  // TV1-1934 — scheme allow-listing. `sanitizeKpiHref` returns `null` for anything
  // that is not an in-app path or an http(s) URL, and a `null` target renders as
  // PLAIN TEXT WITH NO ANCHOR: fail closed. Never fall back to a raw `<a href>` —
  // that fallback is precisely what made a `javascript:` URI, authored in the manual
  // widget builder and shared across the whole account, a STORED XSS.
  //
  // 🔗 `editMode` short-circuits BEFORE the sanitiser (2026-08-19). In the editor a
  // card is an object you are ARRANGING — click to select, drag to move — and a
  // click that navigates away costs the arrangement. The link is the reader's
  // affordance. Gated here rather than by clearing `href`, so the inspector can
  // still show and edit the value.
  const linkTarget = editMode ? null : sanitizeKpiHref( href );

  const valueBody = (
    <Text
      component="div"
      className={`${classes.value} ${TONE_CLASS[bandTone]}`}
      data-testid={componentsTestIds.kpiValue}
      data-value-tone={bandTone}
    >
      {renderedValue}
    </Text>
  );

  /*
   * ⚠️ THE WHOLE TILE IS THE LINK, not just the number (2026-08-19).
   *
   * Ticket owner: *"widgets with link, should navigate the whole widget not just
   * the number?"* — and they are right. A 4×5 card whose only clickable pixel is a
   * number the size of the text is a link you have to aim at, while the card
   * already reads as one object. The delta and the caption are part of the same
   * statement and now navigate with it.
   *
   * The wrapper is OUTSIDE `renderTile` rather than inside it, so the tile keeps
   * its testid and its `data-kpi-state` exactly where every test and every reader
   * with dev-tools open expects them.
   */
  return (
    <WidgetLink
      target={linkTarget}
      testId={componentsTestIds.kpiLink}
      className={classes.valueLink}
    >
      {renderTile(
    <>
      {valueBody}

      {/* ⚠️ ABSENT, not dashed. `no-prior-window` renders NO delta row: ADR-051's
          "unknown is not zero" already forbids fabricating a delta, and a
          placeholder `—` for it is visual noise that reads as breakage. Silence is
          the correct rendering — the value carries the tile on its own. */}
      {kpiState.kind === 'ready' ? (
        <div className={classes.deltaRow} data-testid={componentsTestIds.kpiDelta} data-tone={tone}>
          {delta.kind === 'full' ? (
            <>
              <Text
                component="span"
                className={`${classes.deltaPercent} ${TONE_CLASS[tone]}`}
                data-testid={componentsTestIds.kpiDeltaPercent}
              >
                {ArrowIcon ? <ArrowIcon size={16} stroke={2} data-testid={componentsTestIds.kpiArrow( arrow! )} /> : null}
                {formatKpiDeltaPercent( delta, locale )}
              </Text>

              <Text component="span" className={classes.deltaAbsolute} data-testid={componentsTestIds.kpiDeltaAbsolute}>
                {formatKpiDeltaAbsolute( delta, format, valueOptions )}
              </Text>
            </>
          ) : (
            /* `delta.kind === 'absolute'` — the prior period was exactly 0, so the
               percentage is a division by zero. The ABSOLUTE change is the only
               measured figure, so it takes the primary slot rather than sitting
               next to a dash standing in for the percentage (never `∞`, never a
               fabricated `+100%`). */
            <Text
              component="span"
              className={`${classes.deltaPercent} ${TONE_CLASS[tone]}`}
              data-testid={componentsTestIds.kpiDeltaAbsolute}
              title={t(
                'widget_kpi_delta_percent_undefined',
                'The previous period was zero, so the percentage change is undefined'
              )}
            >
              {ArrowIcon ? <ArrowIcon size={16} stroke={2} data-testid={componentsTestIds.kpiArrow( arrow! )} /> : null}
              {formatKpiDeltaAbsolute( delta, format, valueOptions )}
            </Text>
          )}

        </div>
      ) : null}

      {/*
        ⚠️ ITS OWN LINE, not the tail of the delta row (2026-08-19).

        It used to sit inline after the percentage — "↗ +15  vs. período anterior" —
        which reads as one long sentence and makes the delta compete with the label
        that qualifies it. The reference stacks them (`.val` / `.delta` / `.caption`,
        three block children), and the ticket owner asked for the same: the comparison
        belongs on the line below.

        Rendered under the same `kind === 'ready'` guard as the delta itself: with no
        delta there is nothing being compared, so the words would qualify nothing.
      */}
      {kpiState.kind === 'ready' ? (
        <Text component="div" className={classes.deltaComparison}>
          {t( 'widget_kpi_delta_vs_previous', 'vs. previous period' )}
        </Text>
      ) : null}

      {caption ? (
        <Text component="div" className={classes.caption} data-testid={componentsTestIds.kpiCaption}>
          {caption}
        </Text>
      ) : null}

      {partialCoverage ? <PartialCoverageNote t={t} /> : null}
    </>
      )}
    </WidgetLink>
  );
};
